import { normalizePhoneNumber } from "../../utils/phoneUtils";
import { API_BASE_URL } from "./client";
import type { TeamMember } from "./types";

/** Backend `createErrorResponse` uses `error` + `details`, not `message`. */
function readTeamApiError(errorData: unknown, fallback: string): string {
  if (!errorData || typeof errorData !== "object") return fallback;
  const o = errorData as Record<string, unknown>;
  const details = typeof o.details === "string" ? o.details.trim() : "";
  const err = typeof o.error === "string" ? o.error.trim() : "";
  const msg = typeof o.message === "string" ? o.message.trim() : "";
  if (details) return details;
  if (err) return err;
  if (msg) return msg;
  return fallback;
}

const enc = encodeURIComponent;

export const teamApi = {
  getTeamMembers: async (
    orgId: string,
    token: string
  ): Promise<TeamMember[]> => {
    const response = await fetch(`${API_BASE_URL}/v1/org/${enc(orgId)}/team`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch team members");
    }

    const data = await response.json();
    return data.teamMembers || [];
  },

  addTeamMember: async (
    orgId: string,
    memberData: Omit<TeamMember, "createdAt" | "updatedAt" | "phoneVerified">,
    token: string
  ): Promise<{ teamMember: TeamMember; pin: string }> => {
    const normalizedMemberData = {
      ...memberData,
      phoneNumber: memberData.phoneNumber
        ? normalizePhoneNumber(memberData.phoneNumber)
        : undefined,
    };

    const response = await fetch(`${API_BASE_URL}/v1/org/${enc(orgId)}/team/add`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(normalizedMemberData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(readTeamApiError(errorData, "Failed to add team member"));
    }

    const data = await response.json();
    return { teamMember: data.teamMember, pin: data.pin };
  },

  updateTeamMember: async (
    orgId: string,
    username: string,
    updateData: {
      displayName?: string;
      accessLevel?: "CREATOR" | "OWNER" | "ADMIN";
      phoneNumber?: string;
      circleIds?: string[];
    },
    token: string
  ): Promise<{ teamMember: TeamMember }> => {
    const normalizedUpdateData = {
      ...updateData,
      phoneNumber: updateData.phoneNumber
        ? normalizePhoneNumber(updateData.phoneNumber)
        : undefined,
    };

    const response = await fetch(
      `${API_BASE_URL}/v1/org/${enc(orgId)}/team/${enc(username)}/update`,
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(normalizedUpdateData),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        readTeamApiError(errorData, "Failed to update team member")
      );
    }

    const data = await response.json();
    return { teamMember: data.teamMember };
  },

  deleteTeamMember: async (
    orgId: string,
    username: string,
    token: string
  ): Promise<{ message: string; username: string }> => {
    const response = await fetch(
      `${API_BASE_URL}/v1/org/${enc(orgId)}/team/${enc(username)}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        readTeamApiError(errorData, "Failed to remove team member")
      );
    }

    const data = await response.json();
    return data;
  },

  resetTeamMemberPin: async (
    orgId: string,
    username: string,
    token: string
  ): Promise<{
    message: string;
    pin: string;
    deliveryMethods: string[];
    smsNotification: { sent: boolean; phoneNumber: string | null };
  }> => {
    const response = await fetch(
      `${API_BASE_URL}/v1/org/${enc(orgId)}/team/${enc(username)}/reset-pin`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(readTeamApiError(errorData, "Failed to reset PIN"));
    }

    const data = await response.json();
    return data;
  },
};
